import * as React from "react";
import { Button, Label, Text, Textarea, makeStyles } from "@fluentui/react-components";
import { Add24Regular, Subtract24Regular } from "@fluentui/react-icons";
import type { QueryExecutionResult } from "../../types.js";

const useStyles = makeStyles({
  card: {
    border: "1px solid var(--colorNeutralStroke2)",
    borderRadius: "8px",
    padding: "12px",
    display: "grid",
    gap: "8px",
  },
  collapsibleRow: {
    display: "flex",
    gap: "8px",
    alignItems: "flex-start",
  },
  collapsibleField: {
    flex: 1,
    minWidth: 0,
  },
  collapsedFieldLine: {
    flex: 1,
    minWidth: 0,
    height: 0,
    borderTop: "1px solid var(--colorNeutralStroke2)",
  },
  toggleButton: {
    flexShrink: 0,
    position: "relative",
    top: "-15px",
  },
  expandedTextarea: {
    width: "100%",
  },
  expandedTextareaInput: {
    minHeight: "320px",
    height: "320px",
  },
  textarea: {
    minHeight: "220px",
  },
});

export interface QueryDiagnosticsSectionProps {
  runResult: QueryExecutionResult;
}

export function QueryDiagnosticsSection({ runResult }: QueryDiagnosticsSectionProps): JSX.Element {
  const styles = useStyles();
  const [isRequestUrlCollapsed, setIsRequestUrlCollapsed] = React.useState(true);
  const [isHierarchyDiagnosticsCollapsed, setIsHierarchyDiagnosticsCollapsed] = React.useState(true);

  React.useEffect(() => {
    setIsRequestUrlCollapsed(true);
  }, [runResult.requestUrl]);

  React.useEffect(() => {
    setIsHierarchyDiagnosticsCollapsed(true);
  }, [runResult.hierarchyDiagnostics]);

  const hierarchySummary = React.useMemo(() => {
    const diagnostics = runResult.hierarchyDiagnostics;
    if (!diagnostics) {
      return [] as string[];
    }

    const aliases = Array.from(
      new Set([
        ...Object.keys(diagnostics.relationBatchCountByAlias),
        ...Object.keys(diagnostics.relationPageCountByAlias || {}),
        ...Object.keys(diagnostics.relationRowCountByAlias || {}),
      ]),
    ).sort((a, b) => a.localeCompare(b));

    return aliases.map((alias) => {
      const batches = diagnostics.relationBatchCountByAlias[alias] || 0;
      const pages = diagnostics.relationPageCountByAlias[alias] || 0;
      const rows = diagnostics.relationRowCountByAlias[alias] || 0;
      const truncated = diagnostics.relationTruncatedByAlias[alias] ? " | truncated" : "";
      return `${alias}: ${rows} rows | ${batches} batches | ${pages} pages${truncated}`;
    });
  }, [runResult.hierarchyDiagnostics]);

  const hierarchyTruncationText = React.useMemo(() => {
    const diagnostics = runResult.hierarchyDiagnostics;
    if (!diagnostics) {
      return "";
    }

    const truncatedAliases = Object.entries(diagnostics.relationTruncatedByAlias)
      .filter(([, truncated]) => truncated)
      .map(([alias]) => alias)
      .sort((a, b) => a.localeCompare(b));
    const rootTruncated = diagnostics.rootTruncated ? "root" : "";

    if (!rootTruncated && truncatedAliases.length < 1) {
      return "";
    }

    return [rootTruncated, ...truncatedAliases].filter((item) => item.length > 0).join(", ");
  }, [runResult.hierarchyDiagnostics]);

  const diagnosticsParts = [
    runResult.targetName ? `Target: ${runResult.targetName}` : null,
    runResult.status !== undefined ? `Status: ${runResult.status} ${runResult.statusText || ""}`.trim() : null,
    `Rows: ${runResult.rows.length}`,
    runResult.hierarchicalRows ? `Hierarchical rows: ${runResult.hierarchicalRows.length}` : null,
    runResult.durationMs !== undefined ? `Duration: ${runResult.durationMs}ms` : null,
  ].filter((item): item is string => Boolean(item));

  return (
    <div className={styles.card}>
      <Text weight="semibold">Query diagnostics</Text>
      <Text>{diagnosticsParts.join(" | ")}</Text>
      {runResult.requestUrl ? (
        <>
          <Label>Request URL</Label>
          <div className={styles.collapsibleRow}>
            {isRequestUrlCollapsed ? (
              <div className={styles.collapsedFieldLine} aria-hidden="true" />
            ) : (
              <Textarea
                className={`${styles.collapsibleField} ${styles.expandedTextarea}`}
                textarea={{ className: styles.expandedTextareaInput }}
                value={runResult.requestUrl}
                readOnly
              />
            )}
            <Button
              className={styles.toggleButton}
              appearance="subtle"
              icon={isRequestUrlCollapsed ? <Add24Regular /> : <Subtract24Regular />}
              aria-label={isRequestUrlCollapsed ? "Expand Request URL" : "Collapse Request URL"}
              title={isRequestUrlCollapsed ? "Expand" : "Collapse"}
              onClick={() => setIsRequestUrlCollapsed((value) => !value)}
            />
          </div>
        </>
      ) : null}

      {runResult.hierarchyDiagnostics ? (
        <>
          <Label>Hierarchy diagnostics</Label>
          <div className={styles.collapsibleRow}>
            {isHierarchyDiagnosticsCollapsed ? (
              <div className={styles.collapsedFieldLine} aria-hidden="true" />
            ) : (
              <Textarea
                className={`${styles.collapsibleField} ${styles.expandedTextarea}`}
                textarea={{ className: styles.expandedTextareaInput }}
                readOnly
                value={JSON.stringify(runResult.hierarchyDiagnostics, null, 2)}
              />
            )}
            <Button
              className={styles.toggleButton}
              appearance="subtle"
              icon={isHierarchyDiagnosticsCollapsed ? <Add24Regular /> : <Subtract24Regular />}
              aria-label={isHierarchyDiagnosticsCollapsed ? "Expand hierarchy diagnostics" : "Collapse hierarchy diagnostics"}
              title={isHierarchyDiagnosticsCollapsed ? "Expand" : "Collapse"}
              onClick={() => setIsHierarchyDiagnosticsCollapsed((value) => !value)}
            />
          </div>
          {hierarchySummary.length > 0 ? <Text>{hierarchySummary.join("\n")}</Text> : null}
          {hierarchyTruncationText ? <Text>Truncated: {hierarchyTruncationText}</Text> : null}
        </>
      ) : null}

      {runResult.hierarchicalRows ? (
        <>
          <Label>Hierarchical result</Label>
          <Textarea className={styles.textarea} readOnly value={JSON.stringify(runResult.hierarchicalRows, null, 2)} />
        </>
      ) : null}

      <Label>Raw response</Label>
      <Textarea className={styles.textarea} readOnly value={JSON.stringify(runResult.raw, null, 2)} />
    </div>
  );
}
